import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { api } from "@/lib/api";
import { FadeUp, Overline } from "./Reveal";

const STAT_DEFS = [
  { key: "users", label: "Utilisateurs inscrits" },
  { key: "quiz_completed", label: "Quiz & examens réalisés" },
  { key: "ai_messages", label: "Réponses IA apportées" },
  { key: "partners", label: "Partenaires à vos côtés" },
];

function Counter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1700;
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return <span ref={ref}>{n.toLocaleString("fr-FR")}{suffix}</span>;
}

const TEMOIGNAGES = [
  {
    photo: "/temoignages/amina.jpg",
    name: "Amina",
    detail: "Paris — Carte de séjour vie privée et familiale",
    quote: "L'Interprète IA m'a traduit un courrier de la préfecture que je ne comprenais pas. En deux minutes, je savais exactement quoi faire et quelles pièces préparer.",
  },
  {
    photo: "/temoignages/ibrahim.jpg",
    name: "Ibrahim",
    detail: "Lyon — Carte de résident en préparation",
    quote: "Grâce aux rappels automatiques, je n'ai plus jamais raté une échéance. Le guide ANEF m'a accompagné pas à pas pour renouveler mon titre en ligne, sans stress.",
  },
  {
    photo: "/temoignages/lin.jpg",
    name: "Lin",
    detail: "Bordeaux — Étudiante, en route vers la naturalisation",
    quote: "J'ai réussi mon examen civique du premier coup ! Les examens blancs avec les questions officielles 2026 font vraiment toute la différence.",
  },
  {
    photo: "/temoignages/carlos.jpg",
    name: "Carlos",
    detail: "Toulouse — Salarié, carte pluriannuelle",
    quote: "Le Coach civique répond à toutes mes questions, même le soir après le travail. Pour la première fois, j'ai l'impression d'avancer clairement dans mon parcours.",
  },
];

export function Temoignages() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <section id="temoignages" className="bg-slate-50/70 py-20 sm:py-28" data-testid="landing-temoignages">
      <div className="container-integra">
        <FadeUp className="mx-auto max-w-2xl text-center">
          <Overline>Ils nous font confiance</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Des parcours réels, <span className="text-bleu">des progrès concrets.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-500">
            Étudiants, salariés, familles : ils utilisent INTEGRA FRANCE au quotidien pour comprendre,
            se préparer et avancer sereinement.
          </p>
        </FadeUp>

        {stats && (
          <FadeUp delay={0.1}>
            <div className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4" data-testid="landing-stats">
              {STAT_DEFS.map((s, i) => (
                <div key={s.key} className="card-integra px-4 py-6 text-center" data-testid={`landing-stat-${i}`}>
                  <p className="font-display text-3xl font-extrabold text-bleu sm:text-4xl">
                    <Counter value={stats[s.key] || 0} suffix="" />
                  </p>
                  <p className="mt-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[11px] text-slate-400" data-testid="landing-stats-note">
              Chiffres réels de la plateforme, mis à jour en continu.
            </p>
          </FadeUp>
        )}

        <div className="mt-14 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {TEMOIGNAGES.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.09}>
              <figure className="card-integra card-hover relative flex h-full flex-col p-6" data-testid={`temoignage-card-${i}`}>
                <Quote className="absolute right-5 top-5 h-7 w-7 text-bleu-light" aria-hidden />
                <div className="flex gap-1" aria-label="5 étoiles sur 5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-ambre text-ambre" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">
                  « {t.quote} »
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
                  <img
                    src={t.photo}
                    alt={`Portrait de ${t.name}`}
                    loading="lazy"
                    className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-bleu-light"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900">{t.name}</p>
                    <p className="truncate text-[11px] text-slate-400">{t.detail}</p>
                  </div>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
